import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Rating,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  TrendingUp as ImprovementIcon,
  Chat as GeneralIcon,
  MoreHoriz as OtherIcon,
  Feedback as FeedbackIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const categories = [
  { value: 'bug', label: 'Bug Report', icon: <BugIcon sx={{ fontSize: 18 }} />, color: '#ef4444' },
  { value: 'feature', label: 'Feature Request', icon: <FeatureIcon sx={{ fontSize: 18 }} />, color: '#f59e0b' },
  { value: 'improvement', label: 'Improvement', icon: <ImprovementIcon sx={{ fontSize: 18 }} />, color: '#3b82f6' },
  { value: 'general', label: 'General', icon: <GeneralIcon sx={{ fontSize: 18 }} />, color: '#10b981' },
  { value: 'other', label: 'Other', icon: <OtherIcon sx={{ fontSize: 18 }} />, color: '#8b5cf6' },
];

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState<number | null>(4);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!message.trim()) {
      setError('Please enter your feedback message');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await api.post('/feedback', {
        category,
        subject: subject.trim(),
        message: message.trim(),
        rating: rating || 4,
        userAgent: navigator.userAgent,
        page: window.location.pathname,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCategory('general');
    setSubject('');
    setMessage('');
    setRating(4);
    setSubmitting(false);
    setSubmitted(false);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: isDark ? '#1e293b' : '#fff',
          border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        },
      }}
    >
      {submitted ? (
        // Success view
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: alpha('#10b981', 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <SuccessIcon sx={{ fontSize: 36, color: '#10b981' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: isDark ? '#e2e8f0' : '#1e293b' }}>
            Thank You!
          </Typography>
          <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 3 }}>
            Your feedback has been submitted successfully. We appreciate your input and will review it shortly.
          </Typography>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 4,
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
            }}
          >
            Close
          </Button>
        </Box>
      ) : (
        <>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1,
              borderBottom: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FeedbackIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem', color: isDark ? '#e2e8f0' : '#1e293b' }}>
                Send Feedback
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ color: isDark ? '#94a3b8' : '#9ca3af' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 2.5, pb: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Category Selection */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500 }}>
              Category
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mb: 2.5, flexWrap: 'wrap' }}>
              {categories.map((cat) => (
                <Chip
                  key={cat.value}
                  label={cat.label}
                  icon={cat.icon}
                  onClick={() => setCategory(cat.value)}
                  variant={category === cat.value ? 'filled' : 'outlined'}
                  sx={{
                    borderRadius: '8px',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    borderColor: category === cat.value ? cat.color : isDark ? '#475569' : '#d1d5db',
                    bgcolor: category === cat.value ? alpha(cat.color, isDark ? 0.25 : 0.1) : 'transparent',
                    color: category === cat.value ? cat.color : isDark ? '#94a3b8' : '#6b7280',
                    '& .MuiChip-icon': {
                      color: category === cat.value ? cat.color : isDark ? '#64748b' : '#9ca3af',
                    },
                    '&:hover': {
                      bgcolor: alpha(cat.color, isDark ? 0.2 : 0.08),
                      borderColor: cat.color,
                    },
                  }}
                />
              ))}
            </Box>

            {/* Subject */}
            <TextField
              fullWidth
              label="Subject"
              placeholder="Brief summary of your feedback"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: isDark ? '#0f172a' : '#f8fafc',
                },
              }}
            />

            {/* Message */}
            <TextField
              fullWidth
              label="Feedback Message"
              placeholder="Tell us more about your experience, suggestion, or issue..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              rows={4}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: isDark ? '#0f172a' : '#f8fafc',
                },
              }}
            />

            {/* Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500 }}>
                Rate your experience:
              </Typography>
              <Rating
                value={rating}
                onChange={(_, newValue) => setRating(newValue)}
                sx={{
                  '& .MuiRating-iconFilled': { color: '#f59e0b' },
                  '& .MuiRating-iconEmpty': { color: isDark ? '#475569' : '#d1d5db' },
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: `1px solid ${isDark ? '#334155' : '#e5e7eb'}` }}>
            <Button
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                color: isDark ? '#94a3b8' : '#6b7280',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' },
                '&.Mui-disabled': { bgcolor: alpha('#3b82f6', 0.5) },
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default FeedbackDialog;
